import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function BrowseAuthors({ onViewAuthor, onBack }) {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorSearch, setAuthorSearch] = useState('');

  // Client-side filter: case-insensitive substring match on the author name.
  const filteredAuthors = React.useMemo(() => {
    if (!authorSearch || !authorSearch.trim()) return authors;
    const q = authorSearch.trim().toLowerCase();
    return authors.filter(a => (a.author || '').toLowerCase().includes(q));
  }, [authors, authorSearch]);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/authors`);
      if (response.data.success) {
        setAuthors(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching authors:', err);
      setError('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const authorHref = (author) => `/author/${encodeURIComponent(author)}`;

  if (loading) {
    return (
      <div className="browse-authors">
        <div className="browse-authors-header">
          <button className="author-back-btn" onClick={onBack}>← Back</button>
          <h1>✍️ Browse Authors</h1>
        </div>
        <div className="browse-authors-loading">Loading authors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="browse-authors">
        <div className="browse-authors-header">
          <button className="author-back-btn" onClick={onBack}>← Back</button>
          <h1>✍️ Browse Authors</h1>
        </div>
        <div className="browse-authors-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="browse-authors">
      <div className="browse-authors-header">
        <button className="author-back-btn" onClick={onBack}>← Back</button>
        <h1>✍️ Browse Authors</h1>
        <span className="browse-authors-count">{authors.length} author{authors.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="browse-authors-search-row">
        <input
          type="search"
          className="browse-authors-search-input"
          placeholder="🔍 Search authors by name..."
          value={authorSearch}
          onChange={(e) => setAuthorSearch(e.target.value)}
        />
        {authorSearch && (
          <button
            className="browse-authors-search-clear"
            onClick={() => setAuthorSearch('')}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      <div className="browse-authors-search-row">
        <input
          type="search"
          className="browse-authors-search-input"
          placeholder="🔍 Search authors by name..."
          value={authorSearch}
          onChange={(e) => setAuthorSearch(e.target.value)}
        />
        {authorSearch && (
          <button
            className="browse-authors-search-clear"
            onClick={() => setAuthorSearch('')}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {authors.length === 0 ? (
        <div className="browse-authors-empty">
          <div className="browse-authors-empty-icon">📖</div>
          <h3>No authors found</h3>
          <p>Be the first to write a story!</p>
        </div>
      ) : filteredAuthors.length === 0 ? (
        <div className="browse-authors-empty">
          <div className="browse-authors-empty-icon">🔍</div>
          <h3>No authors match "{authorSearch}"</h3>
          <p>Try a different name, or clear the search.</p>
        </div>
      ) : (
        <div className="browse-authors-grid">
          {filteredAuthors.map(author => (
            <Link
              key={author.author}
              to={authorHref(author.author)}
              className="browse-author-card"
              title={`See all stories by ${author.author}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                if (onViewAuthor) {
                  e.preventDefault();
                  onViewAuthor(author.author);
                }
              }}
            >
              <div className="browse-author-avatar">
                {author.author.charAt(0).toUpperCase()}
              </div>
              <div className="browse-author-info">
                <h3>{author.author}</h3>
                <div className="browse-author-stats">
                  <span>📖 {author.story_count} {author.story_count === 1 ? 'story' : 'stories'}</span>
                  <span>❤️ {author.total_likes || 0}</span>
                  <span>👁️ {author.total_views || 0}</span>
                </div>
                <span className="browse-author-latest">
                  Latest: {formatDate(author.latest_story_date)}
                </span>
              </div>
              <div className="browse-author-arrow">→</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default BrowseAuthors;